import {
	CSSProperties,
	FunctionComponent,
	RefObject,
	useCallback,
	useEffect,
	useLayoutEffect,
	useRef,
	useState,
} from "react";
import ReactDOM from "react-dom";

import { IconDefinition } from "@fortawesome/fontawesome-svg-core";

import { SearchField } from "@/components/SearchField/SearchField";

import { getComposedEventTarget } from "@/utils/getComposedEventTarget";

import { FooterPopoverSection } from "../utils/editorFooter.types";

import { PopoverItemRow } from "./PopoverItemRow";

const POPOVER_WIDTH = 320;
const VIEWPORT_GUTTER = 8;
const ANCHOR_GAP = 4;

interface InsertPopoverProps {
	anchorRef: RefObject<HTMLElement>;
	sections: FooterPopoverSection[];
	search: string;
	onSearchChange: (value: string) => void;
	searchPlaceholder: string;
	emptyLabel: string;
	itemIcon: IconDefinition;
	itemIconWrapClassName: string;
	onClose: () => void;
}

/**
 * Drop-up popover shell of the editor footer: portaled to the body so the
 * scrolling node panel cannot clip it, anchored above its trigger button and
 * closed on outside click or Escape.
 */
export const InsertPopover: FunctionComponent<InsertPopoverProps> = ({
	anchorRef,
	sections,
	search,
	onSearchChange,
	searchPlaceholder,
	emptyLabel,
	itemIcon,
	itemIconWrapClassName,
	onClose,
}) => {
	const popoverRef = useRef<HTMLDivElement>(null);
	const [style, setStyle] = useState<CSSProperties>({});

	const updatePosition = useCallback(() => {
		const anchor = anchorRef.current;

		if (!anchor) {
			return;
		}

		const rect = anchor.getBoundingClientRect();
		let left = rect.left;

		if (left + POPOVER_WIDTH > window.innerWidth - VIEWPORT_GUTTER) {
			left = rect.right - POPOVER_WIDTH;
		}

		if (left < VIEWPORT_GUTTER) {
			left = VIEWPORT_GUTTER;
		}

		// eslint-disable-next-line @eslint-react/hooks-extra/no-direct-set-state-in-use-effect
		setStyle({
			position: "fixed",
			bottom: window.innerHeight - rect.top + ANCHOR_GAP,
			left,
			width: POPOVER_WIDTH,
		});
	}, [anchorRef]);

	useLayoutEffect(() => {
		updatePosition();
		window.addEventListener("scroll", updatePosition, true);
		window.addEventListener("resize", updatePosition);

		return () => {
			window.removeEventListener("scroll", updatePosition, true);
			window.removeEventListener("resize", updatePosition);
		};
	}, [updatePosition]);

	useEffect(() => {
		const handleMouseDown = (event: MouseEvent) => {
			const target = getComposedEventTarget(event);

			if (popoverRef.current?.contains(target) || anchorRef.current?.contains(target)) {
				return;
			}

			onClose();
		};

		const handleKeyDown = (event: KeyboardEvent) => {
			if (event.key === "Escape") {
				onClose();
			}
		};

		document.addEventListener("mousedown", handleMouseDown);
		document.addEventListener("keydown", handleKeyDown);

		return () => {
			document.removeEventListener("mousedown", handleMouseDown);
			document.removeEventListener("keydown", handleKeyDown);
		};
	}, [anchorRef, onClose]);

	useEffect(() => {
		popoverRef.current?.querySelector("input")?.focus();
	}, []);

	const isEmpty = sections.every((section) => section.items.length === 0);

	return ReactDOM.createPortal(
		<div
			ref={popoverRef}
			className="flex flex-col max-h-[360px] overflow-hidden bg-white border border-border-light rounded-lg shadow-lg z-[10000]"
			data-test="editor-footer-popover"
			style={style}
		>
			<div className="p-2.5 border-b border-border-light">
				<SearchField
					handleChange={(event) => onSearchChange(event.target.value)}
					name="editorFooterSearch"
					placeholder={searchPlaceholder}
					value={search}
				/>
			</div>

			<div className="overflow-y-auto p-1">
				{isEmpty && (
					<div className="px-2.5 py-3 text-xs text-text-meta text-center">{emptyLabel}</div>
				)}
				{sections.map(
					(section) =>
						section.items.length > 0 && (
							<div key={section.label ?? "default"}>
								{section.label && (
									<div className="text-[10px] uppercase tracking-wider font-bold text-text-meta px-2 pt-2 pb-1">
										{section.label}
									</div>
								)}
								{section.items.map((item) => (
									<PopoverItemRow
										key={item.key}
										icon={itemIcon}
										iconWrapClassName={itemIconWrapClassName}
										item={item}
									/>
								))}
							</div>
						)
				)}
			</div>
		</div>,
		document.body
	);
};
